import { useState, useEffect } from "react";
import { Modal, Checkbox, message } from "antd";
import { IAggregatorUser } from "../hooks/useGetAggregatorUsers";
import { IAggregatorRole } from "../hooks/useGetAggregatorRoles";
import {
  useAddAggregatorUserRole,
  useRemoveAggregatorUserRole,
} from "../hooks/useAssignAggregatorUserRole";

interface IEditAggregatorUserRoleModal {
  isOpen: boolean;
  onClose: () => void;
  user: IAggregatorUser | undefined;
  roles: IAggregatorRole[];
  rolesLoading: boolean;
  aggregatorId: number;
  onSuccess: () => void;
}

const EditAggregatorUserRoleModal = ({
  isOpen,
  onClose,
  user,
  roles,
  rolesLoading,
  aggregatorId,
  onSuccess,
}: IEditAggregatorUserRoleModal) => {
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const { mutate: addRoles, result: addResult } = useAddAggregatorUserRole();
  const { mutate: removeRoles, result: removeResult } =
    useRemoveAggregatorUserRole();

  const assignableRoleValues = roles.map(({ value }) => value);
  const originalRoles = (user?.roles ?? []).filter((role) =>
    assignableRoleValues.includes(role),
  );
  const hasChanges =
    selectedRoles.length !== originalRoles.length ||
    selectedRoles.some((role) => !originalRoles.includes(role));

  useEffect(() => {
    if (!isOpen || rolesLoading) {
      return;
    }
    setSelectedRoles(originalRoles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, rolesLoading, user]);

  const handleOk = async () => {
    if (!user) {
      return;
    }

    const rolesToAdd = selectedRoles.filter(
      (role) => !originalRoles.includes(role),
    );
    const rolesToRemove = originalRoles.filter(
      (role) => !selectedRoles.includes(role),
    );

    try {
      const results = await Promise.allSettled([
        rolesToAdd.length > 0
          ? addRoles(
              { userId: user.userId, aggregatorId, roleNames: rolesToAdd },
              { throwOnError: true },
            )
          : Promise.resolve(),
        rolesToRemove.length > 0
          ? removeRoles(
              { userId: user.userId, aggregatorId, roleNames: rolesToRemove },
              { throwOnError: true },
            )
          : Promise.resolve(),
      ]);

      const failed = results.some((result) => result.status === "rejected");
      if (failed) {
        message.error(
          "Some role changes could not be saved. Please review and try again.",
        );
      } else {
        message.success("User role(s) updated successfully.");
      }
      onSuccess();
      onClose();
    } catch {
      // unexpected error outside the mutations
    }
  };

  return (
    <Modal
      title="Update User's Role(s)"
      open={isOpen}
      onCancel={onClose}
      onOk={handleOk}
      okText="Update"
      okButtonProps={{ disabled: !hasChanges }}
      confirmLoading={addResult.isLoading || removeResult.isLoading}
      destroyOnClose
    >
      <Checkbox.Group
        style={{ display: "flex", flexDirection: "column" }}
        value={selectedRoles}
        options={roles?.map(({ name, value }) => ({ label: name, value }))}
        onChange={(checked) => setSelectedRoles(checked as string[])}
      />
    </Modal>
  );
};

export default EditAggregatorUserRoleModal;
